import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getSocietySettings, updateSocietySetting, resetSocietySetting, getApprovedParsers } from '../services/api';
import { HiChevronRight } from '../components/Icons';
import './AdminSocietySettings.css';

type SettingOption = { value: string; label: string };

type SettingDef = {
  key: string;
  label: string;
  description: string;
  defaultValue: string;
  options: SettingOption[];
};

const SETTING_DEFS: SettingDef[] = [
  {
    key: 'member_approval_by',
    label: 'Member Approval',
    description: 'Who approves new family members',
    defaultValue: 'society_admin',
    options: [
      { value: 'society_admin', label: 'Society Admin' },
      { value: 'flat_owner', label: 'Flat Owner' },
      { value: 'both', label: 'Both' },
    ],
  },
  {
    key: 'tenant_approval_by',
    label: 'Tenant Approval',
    description: 'Who approves new tenants',
    defaultValue: 'society_admin',
    options: [
      { value: 'society_admin', label: 'Society Admin' },
      { value: 'flat_owner', label: 'Flat Owner' },
      { value: 'both', label: 'Both' },
    ],
  },
  {
    key: 'tenant_can_add_family',
    label: 'Tenant Family',
    description: 'Can tenants add their own family members',
    defaultValue: 'yes',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    key: 'maintenance_bill_visible_to',
    label: 'Bill Visibility',
    description: 'Who can see maintenance bills (society-wide default)',
    defaultValue: 'all_members',
    options: [
      { value: 'primary_owner', label: 'Primary Owner' },
      { value: 'primary_tenant', label: 'Primary Tenant' },
      { value: 'owner_and_family', label: 'Owner & Family' },
      { value: 'tenant_and_family', label: 'Tenant & Family' },
      { value: 'all_members', label: 'All Members' },
    ],
  },
  {
    key: 'maintenance_bill_notify',
    label: 'Bill Notifications',
    description: 'Who receives push notifications for bills (society-wide default)',
    defaultValue: 'same_as_visible',
    options: [
      { value: 'same_as_visible', label: 'Same as Visibility' },
      { value: 'primary_owner', label: 'Primary Owner' },
      { value: 'primary_tenant', label: 'Primary Tenant' },
      { value: 'owner_and_family', label: 'Owner & Family' },
      { value: 'tenant_and_family', label: 'Tenant & Family' },
      { value: 'all_members', label: 'All Members' },
    ],
  },
  {
    key: 'removal_approval_by',
    label: 'Removal Approval',
    description: 'Who approves member removals',
    defaultValue: 'society_admin',
    options: [
      { value: 'society_admin', label: 'Society Admin' },
      { value: 'flat_owner', label: 'Flat Owner' },
      { value: 'self', label: 'Self' },
    ],
  },
  {
    key: 'reconciliation_enabled',
    label: 'Reconciliation Module',
    description: 'Enable Bank Ledger & Reconciliation pages for this society',
    defaultValue: 'no',
    options: [
      { value: 'yes', label: 'Enabled' },
      { value: 'no', label: 'Disabled' },
    ],
  },
  {
    key: 'guard_can_see_family_members',
    label: 'Guard Family Contacts',
    description: 'When enabled, guards see family members of owner/tenant during check-in. When disabled, only owner and tenant contacts are shown.',
    defaultValue: 'no',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
];

const OTHER_CHECKIN_NOTIFY_ROLES_KEY = 'other_checkin_notify_roles';
const OTHER_CHECKIN_NOTIFY_ROLES_DEFAULT = '["society_staff"]';

const OTHER_CHECKIN_ROLE_OPTIONS: SettingOption[] = [
  { value: 'society_staff', label: 'Society Staff' },
  { value: 'president', label: 'President' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'secretary', label: 'Secretary' },
  { value: 'body_member', label: 'Body Member' },
  { value: 'society_admin', label: 'Society Admin' },
  { value: 'chairman', label: 'Chairman' },
  { value: 'committee', label: 'Committee' },
  { value: 'admin', label: 'Admin' },
  { value: 'moderator', label: 'Moderator' },
];

function parseRolesJson(raw: string | undefined): string[] {
  if (!raw) return ['society_staff'];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return ['society_staff'];
    return parsed.map(String).map((r) => r.toLowerCase()).filter(Boolean);
  } catch {
    return ['society_staff'];
  }
}

function mergeSettingsFromApi(data: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = { ...data };
  for (const def of SETTING_DEFS) {
    const v = out[def.key];
    if (v === undefined || v === '') {
      out[def.key] = def.defaultValue;
    }
  }
  if (!out[OTHER_CHECKIN_NOTIFY_ROLES_KEY]) {
    out[OTHER_CHECKIN_NOTIFY_ROLES_KEY] = OTHER_CHECKIN_NOTIFY_ROLES_DEFAULT;
  } else {
    out[OTHER_CHECKIN_NOTIFY_ROLES_KEY] = JSON.stringify(
      parseRolesJson(out[OTHER_CHECKIN_NOTIFY_ROLES_KEY]),
    );
  }
  return out;
}

const AdminSocietySettings: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const societyId = id ? parseInt(id, 10) : NaN;
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [parserOptions, setParserOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const flashSaved = (key: string) => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    setSavedKey(key);
    savedTimerRef.current = setTimeout(() => {
      setSavedKey(null);
      savedTimerRef.current = null;
    }, 2000);
  };

  useEffect(() => {
    if (isNaN(societyId)) {
      setError('Invalid society');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const [settingsRes, parsersRes] = await Promise.all([
          getSocietySettings(societyId),
          getApprovedParsers().catch(() => ({ success: false, data: [] })),
        ]);
        if (cancelled) return;
        if (!settingsRes.success) {
          setError('Failed to load settings');
          return;
        }
        setSettings(mergeSettingsFromApi(settingsRes.data));
        if (parsersRes.success) {
          setParserOptions([
            { value: '', label: 'Default (Auto-detect)' },
            ...parsersRes.data.map((p: any) => ({ value: String(p.id), label: p.name })),
          ]);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load settings');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [societyId]);

  const defByKey = (key: string) => SETTING_DEFS.find((d) => d.key === key);

  const handleChange = async (key: string, newValue: string) => {
    if (isNaN(societyId)) return;
    const def = defByKey(key);
    const previous = settings[key];
    const fallback =
      key === OTHER_CHECKIN_NOTIFY_ROLES_KEY
        ? OTHER_CHECKIN_NOTIFY_ROLES_DEFAULT
        : def?.defaultValue ?? '';
    setSettings((prev) => ({ ...prev, [key]: newValue }));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSavingKey(key);
    try {
      const res = await updateSocietySetting(societyId, key, newValue);
      if (!res.success) {
        throw new Error(res.message || 'Update failed');
      }
      setSettings(mergeSettingsFromApi(res.data));
      flashSaved(key);
    } catch (e: unknown) {
      setSettings((prev) => ({ ...prev, [key]: previous ?? fallback }));
      setRowErrors((prev) => ({
        ...prev,
        [key]: e instanceof Error ? e.message : 'Could not save',
      }));
    } finally {
      setSavingKey(null);
    }
  };

  const handleReset = async (key: string) => {
    if (isNaN(societyId)) return;
    const def = defByKey(key);
    if (!def && key !== OTHER_CHECKIN_NOTIFY_ROLES_KEY) return;
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSavingKey(key);
    try {
      const res = await resetSocietySetting(societyId, key);
      if (!res.success) {
        throw new Error(res.message || 'Reset failed');
      }
      setSettings(mergeSettingsFromApi(res.data));
      flashSaved(key);
    } catch (e: unknown) {
      setRowErrors((prev) => ({
        ...prev,
        [key]: e instanceof Error ? e.message : 'Could not reset',
      }));
    } finally {
      setSavingKey(null);
    }
  };

  const selectedOtherCheckInRoles = parseRolesJson(settings[OTHER_CHECKIN_NOTIFY_ROLES_KEY]);

  const toggleOtherCheckInRole = (role: string, checked: boolean) => {
    const next = checked
      ? (selectedOtherCheckInRoles.includes(role)
          ? selectedOtherCheckInRoles
          : selectedOtherCheckInRoles.concat(role))
      : selectedOtherCheckInRoles.filter((r) => r !== role);
    if (next.length === 0) {
      setRowErrors((prev) => ({
        ...prev,
        [OTHER_CHECKIN_NOTIFY_ROLES_KEY]: 'Select at least one role',
      }));
      return;
    }
    void handleChange(OTHER_CHECKIN_NOTIFY_ROLES_KEY, JSON.stringify(next));
  };

  if (loading) {
    return <div className="admin-society-settings-loading">Loading settings…</div>;
  }

  if (isNaN(societyId)) {
    return (
      <div className="admin-society-settings">
        <Link to="/admin/societies" className="admin-society-settings-back">
          ← Back to societies
        </Link>
        <div className="admin-society-settings-error">{error || 'Invalid society'}</div>
      </div>
    );
  }

  if (error && Object.keys(settings).length === 0) {
    return (
      <div className="admin-society-settings">
        <Link to={`/admin/societies/${societyId}`} className="admin-society-settings-back">
          ← Back to society
        </Link>
        <div className="admin-society-settings-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-society-settings">
      <Link to={`/admin/societies/${societyId}`} className="admin-society-settings-back">
        ← Back to society
      </Link>

      <header className="admin-society-settings-header">
        <h1 className="admin-society-settings-title">Society Settings</h1>
        <p className="admin-society-settings-intro">
          Configure approval flows, bill visibility, and notifications for this society. Changes save automatically.
        </p>
        <Link
          to={`/admin/societies/${societyId}`}
          className="admin-society-settings-open-society"
        >
          Open society overview <HiChevronRight size={18} aria-hidden />
        </Link>
      </header>

      {parserOptions.length > 0 && (
        <section className="admin-society-settings-card" aria-label="Bank statement parser" style={{ marginBottom: '24px' }}>
          <ul className="admin-society-settings-list">
            <li className="admin-society-settings-row">
              <div className="admin-society-settings-row-text">
                <div className="admin-society-settings-row-label">Bank Statement Parser</div>
                <p className="admin-society-settings-row-desc">
                  Select the parser format that matches this society's bank statement layout.{' '}
                  <Link to="/admin/parser-builder" style={{ color: '#1a1a2e', fontWeight: 500 }}>
                    Manage parsers →
                  </Link>
                </p>
                {rowErrors['statement_parser_id'] ? (
                  <p className="admin-society-settings-row-error" role="alert">
                    {rowErrors['statement_parser_id']}
                  </p>
                ) : null}
              </div>
              <div className="admin-society-settings-row-controls">
                <select
                  id="setting-statement_parser_id"
                  className="admin-society-settings-select"
                  value={settings['statement_parser_id'] ?? ''}
                  disabled={savingKey === 'statement_parser_id'}
                  onChange={(e) => void handleChange('statement_parser_id', e.target.value)}
                  aria-label="Bank Statement Parser"
                >
                  {parserOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {settings['statement_parser_id'] && settings['statement_parser_id'] !== '' ? (
                  <button
                    type="button"
                    className="admin-society-settings-reset"
                    disabled={savingKey === 'statement_parser_id'}
                    onClick={() => void handleReset('statement_parser_id')}
                  >
                    Reset
                  </button>
                ) : null}
                {savingKey === 'statement_parser_id' ? (
                  <span className="admin-society-settings-status admin-society-settings-status-saving">Saving…</span>
                ) : savedKey === 'statement_parser_id' ? (
                  <span className="admin-society-settings-status admin-society-settings-status-saved" role="status">Saved</span>
                ) : null}
              </div>
            </li>
          </ul>
        </section>
      )}

      <section className="admin-society-settings-card" aria-label="Other check-in notifications" style={{ marginBottom: '24px' }}>
        <ul className="admin-society-settings-list">
          <li className="admin-society-settings-row admin-society-settings-row-stack">
            <div className="admin-society-settings-row-text">
              <div className="admin-society-settings-row-label">Other Check-in Notify Roles</div>
              <p className="admin-society-settings-row-desc">
                TappyTray admin only. Select which society roles receive Office / Common Area (Other)
                visitor requests from guards. Default is Society Staff.
              </p>
              {rowErrors[OTHER_CHECKIN_NOTIFY_ROLES_KEY] ? (
                <p className="admin-society-settings-row-error" role="alert">
                  {rowErrors[OTHER_CHECKIN_NOTIFY_ROLES_KEY]}
                </p>
              ) : null}
            </div>
            <div className="admin-society-settings-role-grid">
              {OTHER_CHECKIN_ROLE_OPTIONS.map((opt) => {
                const checked = selectedOtherCheckInRoles.includes(opt.value);
                return (
                  <label key={opt.value} className="admin-society-settings-role-option">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={savingKey === OTHER_CHECKIN_NOTIFY_ROLES_KEY}
                      onChange={(e) => toggleOtherCheckInRole(opt.value, e.target.checked)}
                    />
                    <span>{opt.label}</span>
                  </label>
                );
              })}
            </div>
            <div className="admin-society-settings-row-controls" style={{ marginTop: 12 }}>
              {JSON.stringify(selectedOtherCheckInRoles) !== OTHER_CHECKIN_NOTIFY_ROLES_DEFAULT ? (
                <button
                  type="button"
                  className="admin-society-settings-reset"
                  disabled={savingKey === OTHER_CHECKIN_NOTIFY_ROLES_KEY}
                  onClick={() => void handleReset(OTHER_CHECKIN_NOTIFY_ROLES_KEY)}
                >
                  Reset
                </button>
              ) : null}
              {savingKey === OTHER_CHECKIN_NOTIFY_ROLES_KEY ? (
                <span className="admin-society-settings-status admin-society-settings-status-saving">Saving…</span>
              ) : savedKey === OTHER_CHECKIN_NOTIFY_ROLES_KEY ? (
                <span className="admin-society-settings-status admin-society-settings-status-saved" role="status">Saved</span>
              ) : null}
            </div>
          </li>
        </ul>
      </section>

      <section className="admin-society-settings-card" aria-label="Society configuration">
        <ul className="admin-society-settings-list">
          {SETTING_DEFS.map((def) => {
            const raw = settings[def.key] ?? def.defaultValue;
            const known = def.options.some((o) => o.value === raw);
            const selectOptions = known
              ? def.options
              : [{ value: raw, label: `${raw} (current)` }, ...def.options];
            const currentEffective = raw;
            const showReset = currentEffective !== def.defaultValue;
            const rowErr = rowErrors[def.key];
            const isSaving = savingKey === def.key;
            const justSaved = savedKey === def.key;

            return (
              <li key={def.key} className="admin-society-settings-row">
                <div className="admin-society-settings-row-text">
                  <div className="admin-society-settings-row-label">{def.label}</div>
                  <p className="admin-society-settings-row-desc">{def.description}</p>
                  {rowErr ? (
                    <p className="admin-society-settings-row-error" role="alert">
                      {rowErr}
                    </p>
                  ) : null}
                </div>
                <div className="admin-society-settings-row-controls">
                  <select
                    id={`setting-${def.key}`}
                    className="admin-society-settings-select"
                    value={raw}
                    disabled={isSaving}
                    onChange={(e) => void handleChange(def.key, e.target.value)}
                    aria-label={def.label}
                  >
                    {selectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {showReset ? (
                    <button
                      type="button"
                      className="admin-society-settings-reset"
                      disabled={isSaving}
                      onClick={() => void handleReset(def.key)}
                    >
                      Reset
                    </button>
                  ) : null}
                  {isSaving ? (
                    <span className="admin-society-settings-status admin-society-settings-status-saving">
Saving…
                    </span>
                  ) : justSaved ? (
                    <span className="admin-society-settings-status admin-society-settings-status-saved" role="status">
                      Saved
                    </span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
};

export default AdminSocietySettings;
